// src/components/pages/CuestionarioPreguntas.jsx
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiCheckCircle, FiXCircle, FiHelpCircle } from "react-icons/fi";
import AlertDialog from "../Components/Shared/AlertDialog";
import {
  getCuestionarioById,
  getPreguntasByCuestionario,
  updatePreguntas,
} from "../Stores/cuestionariosStore";

const CuestionarioPreguntas = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const cuestionario = getCuestionarioById(id);

  const [preguntas, setPreguntas] = useState(
    () => getPreguntasByCuestionario(id) || []
  );
  const [nuevoTexto, setNuevoTexto] = useState("");

  const [modal, setModal] = useState({
    open: false,
    mode: null, // 'question' | 'answers' | 'delete'
    questionId: null,
  });
  const [alertDialog, setAlertDialog] = useState({ open: false, title: "Información", message: "", variant: "info" });

  const [draftQuestionText, setDraftQuestionText] = useState("");
  const [draftAnswers, setDraftAnswers] = useState([]);

  // ---------- NUEVA PREGUNTA ----------
  const handleAgregarPregunta = (e) => {
    e.preventDefault();
    const texto = nuevoTexto.trim();
    if (!texto) return;

    const nextId =
      preguntas.length > 0
        ? Math.max(...preguntas.map((p) => p.id)) + 1
        : 1;

    const nueva = {
      id: nextId,
      texto,
      respuestas: [
        {
          id: 1,
          texto: "Respuesta incorrecta (demo).",
          correcta: false,
        },
        {
          id: 2,
          texto: "Respuesta correcta (demo).",
          correcta: true,
        },
      ],
    };

    const updated = [...preguntas, nueva];
    setPreguntas(updated);
    updatePreguntas(id, updated);
    setNuevoTexto("");
  };

  // ---------- MODAL HELPERS ----------
  const openEditQuestion = (questionId) => {
    const q = preguntas.find((p) => p.id === questionId);
    if (!q) return;
    setDraftQuestionText(q.texto);
    setModal({ open: true, mode: "question", questionId });
  };

  const openEditAnswers = (questionId) => {
    const q = preguntas.find((p) => p.id === questionId);
    if (!q) return;
    setDraftAnswers(q.respuestas.map((r) => ({ ...r })));
    setModal({ open: true, mode: "answers", questionId });
  };

  const openDeleteQuestion = (questionId) => {
    setModal({ open: true, mode: "delete", questionId });
  };

  const closeModal = () => {
    setModal({ open: false, mode: null, questionId: null });
    setDraftQuestionText("");
    setDraftAnswers([]);
  };

  // Guardar texto de pregunta
  const applyQuestionEdit = () => {
    const texto = draftQuestionText.trim();
    if (!texto) return;
    const updated = preguntas.map((p) =>
      p.id === modal.questionId ? { ...p, texto } : p
    );
    setPreguntas(updated);
    updatePreguntas(id, updated);
    closeModal();
  };

  // Cambiar texto de respuestas en draft
  const handleDraftAnswerChange = (index, value) => {
    setDraftAnswers((prev) =>
      prev.map((a, i) => (i === index ? { ...a, texto: value } : a))
    );
  };

  // marcar cuál es la respuesta correcta
  const handleSetCorrect = (index) => {
    setDraftAnswers((prev) =>
      prev.map((a, i) => ({ ...a, correcta: i === index }))
    );
  };

  // agregar más respuestas (máx. 6)
  const handleAddDraftAnswer = () => {
    setDraftAnswers((prev) => {
      if (prev.length >= 6) return prev;
      const nextId =
        prev.length > 0
          ? Math.max(...prev.map((a) => a.id)) + 1
          : 1;
      return [
        ...prev,
        {
          id: nextId,
          texto: "Nueva respuesta demo.",
          correcta: false,
        },
      ];
    });
  };

  // Guardar cambios de respuestas
  const applyAnswersEdit = () => {
    const updated = preguntas.map((p) =>
      p.id === modal.questionId
        ? { ...p, respuestas: [...draftAnswers] }
        : p
    );
    setPreguntas(updated);
    updatePreguntas(id, updated);
    closeModal();
  };

  // Eliminar pregunta
  const confirmDelete = () => {
    const updated = preguntas.filter((p) => p.id !== modal.questionId);
    setPreguntas(updated);
    updatePreguntas(id, updated);
    closeModal();
  };

  return (
    <div className="qe-page">
      <div className="profile-header-title">
        <h1>Agregar Pregunta</h1>
      </div>

      <p className="qe-subtitle">
        Cuestionario:&nbsp;
        <strong>{cuestionario?.nombre ?? "Cuestionario demo"}</strong>
        {cuestionario && (
          <>
            {" "}
            · Versión <strong>{cuestionario.version}</strong>
          </>
        )}
      </p>

      {/* Formulario para nueva pregunta */}
      <form className="qe-form" onSubmit={handleAgregarPregunta}>
        <label className="qe-label" htmlFor="qe-texto">
          Texto de la Pregunta
        </label>
        <input
          id="qe-texto"
          className="qe-input"
          type="text"
          placeholder="Ingresa la pregunta"
          value={nuevoTexto}
          onChange={(e) => setNuevoTexto(e.target.value)}
        />

        <div className="qe-form-actions">
          <button
            type="button"
            className="qe-btn qe-btn-secondary"
            onClick={() =>
              setAlertDialog({
                open: true,
                title: "Información",
                message: "Demo: después de guardar la pregunta puedes modificar las respuestas desde 'Modificar respuestas'.",
                variant: "info"
              })
            }
          >
            Agregar respuesta
          </button>
          <button type="submit" className="qe-btn qe-btn-primary">
            Guardar pregunta
          </button>
        </div>
      </form>

      {/* Lista de preguntas existentes */}
      <h2 className="qe-section-title">Preguntas Existentes</h2>

      <div className="qe-list">
        {preguntas.map((p, index) => (
          <div key={p.id} className="qe-question-card">
            <div className="qe-question-header">
              <div className="qe-question-index">
                <FiHelpCircle className="qe-question-icon" />
                <span className="qe-question-number">
                  {index + 1}.
                </span>
              </div>
              <p className="qe-question-text">{p.texto}</p>
            </div>

            <div className="qe-answers">
              {p.respuestas.map((r) => (
                <div
                  key={r.id}
                  className={
                    "qe-answer " +
                    (r.correcta ? "qe-answer-correct" : "qe-answer-wrong")
                  }
                >
                  {r.correcta ? (
                    <FiCheckCircle className="qe-answer-icon" />
                  ) : (
                    <FiXCircle className="qe-answer-icon" />
                  )}
                  <span>{r.texto}</span>
                </div>
              ))}
            </div>

            <div className="qe-card-actions">
              <button
                type="button"
                className="qe-btn qe-btn-outline"
                onClick={() => openEditQuestion(p.id)}
              >
                Modificar Pregunta
              </button>
              <button
                type="button"
                className="qe-btn qe-btn-outline"
                onClick={() => openEditAnswers(p.id)}
              >
                Modificar Respuestas
              </button>
              <button
                type="button"
                className="qe-btn qe-btn-danger"
                onClick={() => openDeleteQuestion(p.id)}
              >
                Eliminar Pregunta
              </button>
            </div>
          </div>
        ))}

        {preguntas.length === 0 && (
          <p className="qe-empty-state">
            No hay preguntas en este cuestionario.
          </p>
        )}
      </div>

      <div className="qe-footer-back">
        <button
          type="button"
          className="qe-btn qe-btn-outline"
          onClick={() => navigate("/menu/lms/cuestionarios")}
        >
          ← Volver a cuestionarios
        </button>
      </div>

      {/* -------- MODAL -------- */}
      {modal.open && (
        <div className="qe-modal-overlay">
          <div className="qe-modal">
            {modal.mode === "question" && (
              <>
                <h3 className="qe-modal-title">Editar pregunta</h3>
                <p className="qe-modal-text">
                  Modifica el texto de la pregunta seleccionada.
                </p>
                <textarea
                  className="qe-modal-textarea"
                  value={draftQuestionText}
                  onChange={(e) =>
                    setDraftQuestionText(e.target.value)
                  }
                />
                <div className="qe-modal-actions">
                  <button
                    type="button"
                    className="qe-btn qe-btn-secondary"
                    onClick={closeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="qe-btn qe-btn-primary"
                    onClick={applyQuestionEdit}
                  >
                    Guardar cambios
                  </button>
                </div>
              </>
            )}

            {modal.mode === "answers" && (
              <>
                <h3 className="qe-modal-title">Editar respuestas</h3>
                <p className="qe-modal-text">
                  Puedes agregar opciones (máx. 6) y elegir cuál es la
                  respuesta correcta.
                </p>

                <div className="qe-modal-answers-edit">
                  {draftAnswers.map((r, index) => (
                    <div
                      key={r.id}
                      className={
                        "qe-modal-answer-row " +
                        (r.correcta
                          ? "qe-modal-answer-row-correct"
                          : "")
                      }
                    >
                      <div className="qe-modal-answer-main">
                        {r.correcta ? (
                          <FiCheckCircle className="qe-modal-answer-icon" />
                        ) : (
                          <FiXCircle className="qe-modal-answer-icon" />
                        )}
                        <input
                          type="text"
                          className="qe-modal-answer-input"
                          value={r.texto}
                          onChange={(e) =>
                            handleDraftAnswerChange(
                              index,
                              e.target.value
                            )
                          }
                        />
                      </div>

                      <div className="qe-modal-answer-right">
                        <input
                          type="radio"
                          name="correctAnswer"
                          className="qe-modal-answer-radio"
                          checked={r.correcta}
                          onChange={() => handleSetCorrect(index)}
                        />
                        <span className="qe-modal-answer-label">
                          {r.correcta
                            ? "Correcta"
                            : "Marcar como correcta"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="qe-modal-answers-toolbar">
                  <span className="qe-modal-hint">
                    Máximo 6 respuestas por pregunta.
                  </span>
                  <button
                    type="button"
                    className="qe-btn qe-btn-secondary qe-btn-small"
                    disabled={draftAnswers.length >= 6}
                    onClick={handleAddDraftAnswer}
                  >
                    Agregar respuesta
                  </button>
                </div>

                <div className="qe-modal-actions">
                  <button
                    type="button"
                    className="qe-btn qe-btn-secondary"
                    onClick={closeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="qe-btn qe-btn-primary"
                    onClick={applyAnswersEdit}
                  >
                    Guardar cambios
                  </button>
                </div>
              </>
            )}

            {modal.mode === "delete" && (
              <>
                <h3 className="qe-modal-title">
                  Eliminar pregunta del cuestionario
                </h3>
                <p className="qe-modal-text">
                  Esta acción elimina la pregunta de este cuestionario
                  en la demo.
                </p>
                <div className="qe-modal-actions">
                  <button
                    type="button"
                    className="qe-btn qe-btn-secondary"
                    onClick={closeModal}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="qe-btn qe-btn-danger"
                    onClick={confirmDelete}
                  >
                    Eliminar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de alertas estándar LMS */}
      <AlertDialog
        open={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        variant={alertDialog.variant}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        buttonText="Aceptar"
      />
    </div>
  );
};

export default CuestionarioPreguntas;
